import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { useInsumos } from '../../hooks/useInsumos';
import { useReporte, reporteActions } from '../../context/ReporteContext';
import { useTranslation } from 'react-i18next';

interface ModalAgregarInsumoProps {
  isOpen: boolean;
  onClose: () => void;
  insumoParaEditar?: any;
  modoEdicion?: boolean;
}

const ModalAgregarInsumo: React.FC<ModalAgregarInsumoProps> = ({
  isOpen,
  onClose,
  insumoParaEditar,
  modoEdicion = false
}) => {
  const { dispatch } = useReporte();
  const { insumos, cargandoInsumos, errorInsumos } = useInsumos();
  const { t } = useTranslation();

  const [formData, setFormData] = useState({
    insumo_id: 0,
    cantidad: 0,
    nombre_insumo: '',
    unidad: '',
    costo_unitario: 0
  });

  // Estado local para input numérico (permite coma/punto decimal)
  const [localCantidad, setLocalCantidad] = useState(formData.cantidad.toString());

  // Sincronizar estado local cuando cambia formData.cantidad
  useEffect(() => {
    setLocalCantidad(formData.cantidad.toString());
  }, [formData.cantidad]);

  // Cargar datos en modo edición
  useEffect(() => {
    if (modoEdicion && insumoParaEditar) {
      setFormData({
        insumo_id: insumoParaEditar.insumo_id,
        cantidad: insumoParaEditar.cantidad,
        nombre_insumo: insumoParaEditar.nombre_insumo || '',
        unidad: insumoParaEditar.unidad || '',
        costo_unitario: insumoParaEditar.costo_unitario || 0
      });
    } else {
      // Limpiar formulario
      setFormData({
        insumo_id: 0,
        cantidad: 0,
        nombre_insumo: '',
        unidad: '',
        costo_unitario: 0
      });
    }
  }, [modoEdicion, insumoParaEditar, isOpen]);

  const handleInsumoChange = (insumoId: number) => {
    const insumoSeleccionado = insumos.find(i => i.id === insumoId);
    if (insumoSeleccionado) {
      setFormData(prev => ({
        ...prev,
        insumo_id: insumoId,
        nombre_insumo: insumoSeleccionado.descripcion,
        unidad: insumoSeleccionado.unidad_medida,
        costo_unitario: insumoSeleccionado.precio_unitario || 0
      }));
    }
  };

  const handleGuardar = () => {
    // Validaciones
    if (!formData.insumo_id) {
      toast.error(t('supplies.select_supply'));
      return;
    }

    if (formData.cantidad <= 0) {
      toast.error(t('faenas.hours_required'));
      return;
    }

    // Crear objeto insumo
    const nuevoInsumo = {
      id_temporal: modoEdicion 
        ? insumoParaEditar.id_temporal 
        : `insumo_${Date.now()}`,
      insumo_id: formData.insumo_id,
      cantidad: formData.cantidad,
      nombre_insumo: formData.nombre_insumo,
      unidad: formData.unidad,
      costo_unitario: formData.costo_unitario
    };

    if (modoEdicion) {
      // Actualizar insumo existente
      dispatch(reporteActions.actualizarInsumo({ 
        id_temporal: insumoParaEditar.id_temporal, 
        insumo: nuevoInsumo 
      }));
      toast.success(t('supplies.edit_supply'));
    } else {
      dispatch(reporteActions.agregarInsumo(nuevoInsumo));
      toast.success(t('supplies.add_supply'));
    }

    onClose();
  };

  const handleCancelar = () => {
    setFormData({
      insumo_id: 0,
      cantidad: 0,
      nombre_insumo: '',
      unidad: '',
      costo_unitario: 0
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10001] p-4">
      <div className="bg-white rounded-lg p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">
            {modoEdicion ? t('supplies.edit_supply') : t('supplies.add_supply')}
          </h3>
          <button
            onClick={handleCancelar}
            className="text-gray-400 hover:text-gray-600"
          >
            ❌
          </button>
        </div>

        {errorInsumos && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600">❌ {errorInsumos}</p>
          </div>
        )}

        <div className="space-y-4">
          {/* Selector de Insumo */}
          <div>
            <label className="block text-sm font-medium mb-1">
              Insumo *
            </label>
            <select
              value={formData.insumo_id}
              onChange={(e) => handleInsumoChange(parseInt(e.target.value))}
              className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={cargandoInsumos}
            >
              <option value={0}>
                {cargandoInsumos ? t('supplies.loading_supplies') : t('supplies.select_supply')}
              </option>
              {insumos.map((insumo) => (
                <option key={insumo.id} value={insumo.id}>
                  {insumo.codigo} - {insumo.descripcion}
                </option>
              ))}
            </select>
          </div>

          {/* Información del insumo seleccionado */}
          {formData.insumo_id > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-700">
                <strong>Unidad:</strong> {formData.unidad}
              </p>
              {formData.costo_unitario > 0 && (
                <p className="text-sm text-blue-700">
                  <strong>Precio unitario:</strong> ${formData.costo_unitario.toLocaleString('es-CL')}
                </p>
              )}
            </div>
          )}

          {/* Cantidad */}
          <div>
            <label className="block text-sm font-medium mb-1">
              Cantidad * {formData.unidad && `(${formData.unidad})`}
            </label>
            <input
              type="text"
              inputMode="decimal"
              value={localCantidad}
              onChange={(e) => {
                const stringValue = e.target.value;
                setLocalCantidad(stringValue);
                const normalized = stringValue.replace(',', '.');
                const num = parseFloat(normalized);
                if (!isNaN(num) && num >= 0) {
                  setFormData(prev => ({
                    ...prev,
                    cantidad: num
                  }));
                } else if (stringValue === '' || stringValue === '0') {
                  setFormData(prev => ({
                    ...prev,
                    cantidad: 0
                  }));
                }
              }}
              onBlur={() => {
                const normalized = localCantidad.replace(',', '.');
                const num = parseFloat(normalized);
                setLocalCantidad(isNaN(num) ? '0' : num.toString());
              }}
              pattern="[0-9]*[.,]?[0-9]*"
              placeholder="0.0"
              className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Costo total calculado */}
          {formData.cantidad > 0 && formData.costo_unitario > 0 && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <p className="text-sm text-green-700">
                <strong>Costo total:</strong> ${(formData.cantidad * formData.costo_unitario).toLocaleString('es-CL')}
              </p>
            </div>
          )}
        </div>

        {/* Botones */}
        <div className="flex justify-end space-x-3 mt-6">
          <button
            onClick={handleCancelar}
            className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleGuardar}
            disabled={!formData.insumo_id || formData.cantidad <= 0}
            className={`px-4 py-2 rounded-lg text-white ${
              formData.insumo_id && formData.cantidad > 0
                ? 'bg-blue-600 hover:bg-blue-700'
                : 'bg-gray-400 cursor-not-allowed'
            }`}
          >
            {modoEdicion ? t('common.edit') : t('common.add')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ModalAgregarInsumo;
